import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Send, Paperclip, Sparkles, ShieldAlert } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { emailService } from "@/services/emailService";
import { listService } from "@/services/listService";
import { RecipientChips } from "./RecipientChips";
import { TemplateSelector } from "./TemplateSelector";
import type { EmailRecipient, EmailTemplate } from "@/types/email";

interface SendEmailModalProps {
	open: boolean;
	onClose: () => void;
	recipients?: EmailRecipient[];
	job?: {
		id: string;
		title: string;
		description?: string;
		publicDescription?: string;
		skills?: string[];
		company: {
			id: string;
			name: string;
		};
	};
	listId?: string;
	listName?: string;
	defaultTemplate?: string;
}

export const SendEmailModal = ({
	open,
	onClose,
	recipients: initialRecipients,
	job,
	listId,
	listName,
	defaultTemplate,
}: SendEmailModalProps) => {
	const { t, i18n } = useTranslation();
	const { toast } = useToast();
	const { currentOrganization, user } = useAuth();

	const [recipients, setRecipients] = useState<EmailRecipient[]>(initialRecipients || []);
	const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");
	const [attachPdf, setAttachPdf] = useState(false);
	const [language, setLanguage] = useState<'en' | 'fr' | 'nl'>(() => {
		const lang = i18n.language?.substring(0, 2);
		return (lang === 'fr' || lang === 'nl') ? lang : 'en';
	});
	const [isSending, setIsSending] = useState(false);
	const [isLoadingListMembers, setIsLoadingListMembers] = useState(false);
	const [sendProgress, setSendProgress] = useState<{ sent: number; total: number } | null>(null);
	const [descriptionType, setDescriptionType] = useState<'standard' | 'public'>('standard');
	const [isAIGenerating, setIsAIGenerating] = useState(false);
	const [userSignature, setUserSignature] = useState("");
	const [userSenderName, setUserSenderName] = useState("");
	const [userSenderAddress, setUserSenderAddress] = useState("");

	const hasPublicDescription = !!(job?.publicDescription && job.publicDescription.trim());
	const activeJobDescription = descriptionType === 'public' && hasPublicDescription
		? job!.publicDescription!
		: job?.description || "";

	// Load user email settings from profiles.settings
	useEffect(() => {
		if (!open || !user?.id) return;
		supabase
			.from('profiles')
			.select('settings')
			.eq('id', user.id)
			.single()
			.then(({ data }) => {
				const s = data?.settings as any;
				setUserSignature(s?.email_signature || "");
				setUserSenderName(s?.email_sender_name || "");
				setUserSenderAddress(s?.email_sender_address || "");
			});
	}, [open, user?.id]);

	// Load list members if listId is provided
	useEffect(() => {
		if (open && listId && (!initialRecipients || initialRecipients.length === 0)) {
			loadListMembers();
		}
	}, [open, listId]);

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setRecipients(initialRecipients || []);
			setSelectedTemplate(null);
			setSubject("");
			setBody("");
			setAttachPdf(false);
			setSendProgress(null);
			setIsSending(false);
			setDescriptionType('standard');
		}
	}, [open]);

	// Apply template when selected
	useEffect(() => {
		if (!selectedTemplate) return;

		const langSuffix = `_${language}` as const;
		const templateSubject = (selectedTemplate as any)[`subject${langSuffix}`] || selectedTemplate.subject_en;
		const templateBody = (selectedTemplate as any)[`body${langSuffix}`] || selectedTemplate.body_en;

		// Pre-fill known variables
		const vars: Record<string, string> = {};
		if (job) {
			vars.job_title = job.title;
			vars.company_name = job.company.name;
			vars.job_description = activeJobDescription;
			if (job.skills) vars.job_skills = job.skills.join(", ");
		}
		if (user) {
			const profile = user as any;
			vars.sender_name = profile.full_name || profile.email || "";
		}

		let filledSubject = templateSubject;
		let filledBody = templateBody;
		for (const [key, value] of Object.entries(vars)) {
			const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
			filledSubject = filledSubject.replace(regex, value);
			filledBody = filledBody.replace(regex, value);
		}

		setSubject(filledSubject);
		setBody(filledBody);
	}, [selectedTemplate, language, descriptionType]);

	const loadListMembers = async () => {
		if (!listId) return;
		setIsLoadingListMembers(true);
		try {
			const members = await listService.getListMembers(listId);
			const emailRecipients: EmailRecipient[] = members
				.filter(m => m.candidate?.email)
				.map(m => ({
					email: m.candidate!.email,
					name: m.candidate!.name || "",
					candidateId: m.candidate_id,
				}));
			setRecipients(emailRecipients);
		} catch (_error) {
			toast({
				title: t("common.error", "Error"),
				description: t("email.compose.loadMembersFailed", "Failed to load list members"),
				variant: "destructive",
			});
		} finally {
			setIsLoadingListMembers(false);
		}
	};

	const handleRemoveRecipient = (index: number) => {
		setRecipients(prev => prev.filter((_, i) => i !== index));
	};

	const handleAddRecipient = (recipient: EmailRecipient) => {
		setRecipients(prev => [...prev, recipient]);
	};

	const handleAddMultipleRecipients = (newRecipients: EmailRecipient[]) => {
		setRecipients(prev => [...prev, ...newRecipients]);
	};

	const handleTemplateChange = (template: EmailTemplate | null) => {
		setSelectedTemplate(template);
		if (!template) {
			setSubject("");
			setBody("");
		}
	};

	const validRecipients = useMemo(() =>
		recipients.filter(r => r.email && r.email.includes("@")),
		[recipients]
	);

	const emailSendingEnabled = !!currentOrganization?.email_sending_enabled;

	const canSend = emailSendingEnabled && validRecipients.length > 0 && subject.trim() && body.trim() && !isSending;

	const handleSend = async () => {
		if (!canSend || !currentOrganization?.id) return;

		setIsSending(true);
		setSendProgress({ sent: 0, total: validRecipients.length });

		try {
			const isBulk = validRecipients.length > 1;

			const result = await emailService.sendEmail({
				mode: isBulk ? "bulk" : "single",
				template_slug: selectedTemplate?.slug,
				subject,
				message: body,
				recipient: isBulk ? undefined : validRecipients[0].email,
				recipient_name: isBulk ? undefined : validRecipients[0].name,
				recipients: isBulk ? validRecipients.map(r => ({
					email: r.email,
					name: r.name,
					candidate_id: r.candidateId,
					variables: { candidate_name: r.name || "" },
				})) : undefined,
				candidate_id: isBulk ? undefined : validRecipients[0].candidateId,
				job: job ? {
					id: job.id,
					title: job.title,
					description: activeJobDescription,
					publicDescription: hasPublicDescription ? job.publicDescription : undefined,
					description_type: attachPdf ? descriptionType : undefined,
					skills: job.skills,
					company: job.company,
				} : undefined,
				variables: {
					sender_name: (user as any)?.full_name || (user as any)?.email || "",
				},
				language,
				attach_pdf: attachPdf,
				organization_uuid: currentOrganization.id,
				sender_name: userSenderName || undefined,
				sender_address: userSenderAddress || undefined,
				signature: userSignature || undefined,
			});

			if (result.success) {
				const sentCount = result.data?.sent_count || (isBulk ? 0 : 1);
				const failedCount = result.data?.failed_count || 0;

				setSendProgress({ sent: sentCount, total: validRecipients.length });

				// Save to public_messages (saved messages / mailbox) for each candidate recipient
				await saveToPublicMessages(validRecipients);

				toast({
					title: t("email.compose.sent", "Email sent!"),
					description: isBulk
						? t("email.compose.bulkSentDesc", "{{sent}} of {{total}} emails sent successfully", {
							sent: sentCount,
							total: validRecipients.length,
						})
						: t("email.compose.singleSentDesc", "Email sent to {{name}}", {
							name: validRecipients[0].name || validRecipients[0].email,
						}),
				});

				setTimeout(() => onClose(), 1500);
			} else {
				toast({
					title: t("common.error", "Error"),
					description: result.error || t("email.compose.sendFailed", "Failed to send email"),
					variant: "destructive",
				});
			}
		} catch (error: any) {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("email.compose.sendFailed", "Failed to send email"),
				variant: "destructive",
			});
		} finally {
			setIsSending(false);
		}
	};

	const saveToPublicMessages = async (sentRecipients: EmailRecipient[]) => {
		if (!currentOrganization?.id || !user) return;

		try {
			const senderEmail = (user as any)?.email || "";
			const senderName = (user as any)?.full_name || senderEmail;
			const participants = {
				from: [{ address: senderEmail, name: senderName }],
				to: sentRecipients.map(r => ({ address: r.email, name: r.name || "" })),
			};

			const messageKey = `outbound-email-${Date.now()}-${Math.random().toString(36).substring(7)}`;

			const { data: savedMsg, error: msgError } = await supabase
				.from('public_messages')
				.insert({
					original_message_key: messageKey,
					message_type: 'email',
					subject: subject,
					message: body,
					organization_uuid: currentOrganization.id,
					participants,
					include_in_ai_analysis: false,
				})
				.select('id')
				.single();

			if (msgError || !savedMsg) return;

			// Link to candidate records
			const candidateRecipients = sentRecipients.filter(r => r.candidateId);
			if (candidateRecipients.length > 0) {
				await supabase
					.from('public_message_candidate')
					.insert(candidateRecipients.map(r => ({
						public_message_id: savedMsg.id,
						candidate_id: r.candidateId!,
					})));
			}

			// Link to job if present
			if (job) {
				await supabase
					.from('public_message_jobs')
					.insert({
						public_message_id: savedMsg.id,
						job_id: job.id,
					});
			}
		} catch (_error) {
			// Non-critical — don't block the send flow
		}
	};

	const handleAIDraft = async () => {
		if (!job || isAIGenerating) return;

		setIsAIGenerating(true);
		try {
			const { data, error } = await supabase.functions.invoke("draft-email-with-ai", {
				body: {
					job: {
						title: job.title,
						description: activeJobDescription,
						skills: job.skills,
						company: job.company,
					},
					language,
					recipient_name: validRecipients.length === 1 ? validRecipients[0].name : undefined,
					has_signature: !!userSignature,
				},
			});

			if (error)
				throw error;

			if (data?.subject)
				setSubject(data.subject);
			if (data?.body)
				setBody(data.body);
		} catch (_error) {
			toast({
				title: t("common.error", "Error"),
				description: t("email.compose.aiError", "Failed to draft email with AI"),
				variant: "destructive",
			});
		} finally {
			setIsAIGenerating(false);
		}
	};

	const quillModules = useMemo(() => ({
		toolbar: [
			[{ header: [1, 2, 3, false] }],
			["bold", "italic", "underline"],
			[{ list: "ordered" }, { list: "bullet" }],
			["link"],
			["clean"],
		],
	}), []);

	return (
		<Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
			<DialogContent className="max-w-5xl w-full max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Send className="h-5 w-5" />
						{t("email.compose.title", "Send Email")}
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-4 mt-2">
					{!emailSendingEnabled && (
						<Alert variant="destructive">
							<ShieldAlert className="h-4 w-4" />
							<AlertDescription>
								{t(
									"email.compose.sendingDisabled",
									"Email sending isn't enabled for your organization yet. Contact support to activate it."
								)}
							</AlertDescription>
						</Alert>
					)}

					{/* Recipients */}
					<div className="space-y-1.5">
						<Label>{t("email.compose.to", "To")}</Label>
						{isLoadingListMembers ? (
							<div className="flex items-center gap-2 text-sm text-gray-500">
								<Loader2 className="h-4 w-4 animate-spin" />
								{t("email.compose.loadingMembers", "Loading list members...")}
							</div>
						) : (
							<RecipientChips
								recipients={recipients}
								listName={listName}
								onRemove={handleRemoveRecipient}
								onAdd={handleAddRecipient}
								onAddMultiple={handleAddMultipleRecipients}
							/>
						)}
					</div>

					{/* Template Selector */}
					<div className="space-y-1.5">
						<Label>{t("email.compose.template", "Template")}</Label>
						<TemplateSelector
							value={selectedTemplate?.slug}
							onChange={handleTemplateChange}
							language={language}
						/>
					</div>

					{/* Language Toggle */}
					<div className="space-y-1.5">
						<Label>{t("email.compose.language", "Language")}</Label>
						<div className="flex gap-1">
							{(['en', 'fr', 'nl'] as const).map(lang => (
								<Button
									key={lang}
									variant={language === lang ? "default" : "outline"}
									size="sm"
									onClick={() => setLanguage(lang)}
									className="min-w-[40px]"
								>
									{lang.toUpperCase()}
								</Button>
							))}
						</div>
					</div>

					{/* Description Type (only if job has public description) */}
					{job && hasPublicDescription && (
						<div className="space-y-1.5">
							<Label>{t("email.compose.descriptionType", "Job Description Version")}</Label>
							<div className="flex gap-1">
								<Button
									variant={descriptionType === 'standard' ? "default" : "outline"}
									size="sm"
									onClick={() => setDescriptionType('standard')}
								>
									{t("email.compose.standardDescription", "Standard")}
								</Button>
								<Button
									variant={descriptionType === 'public' ? "default" : "outline"}
									size="sm"
									onClick={() => setDescriptionType('public')}
								>
									{t("email.compose.publicDescription", "Public")}
								</Button>
							</div>
						</div>
					)}

					{/* Subject */}
					<div className="space-y-1.5">
						<Label>{t("email.compose.subject", "Subject")}</Label>
						<Input
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
							placeholder={t("email.compose.subjectPlaceholder", "Email subject...")}
						/>
					</div>

					{/* Body Editor */}
					<div className="space-y-1.5">
						<div className="flex items-center justify-between">
							<Label>{t("email.compose.message", "Message")}</Label>
							{job && (
								<Button
									variant="outline"
									size="sm"
									onClick={handleAIDraft}
									disabled={isAIGenerating}
									className="flex items-center gap-1.5 text-xs"
								>
									{isAIGenerating ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<Sparkles className="h-3.5 w-3.5" />
									)}
									{isAIGenerating
										? t("email.compose.aiGenerating", "Drafting...")
										: t("email.compose.aiDraft", "Draft with AI")}
								</Button>
							)}
						</div>
						<div className="border rounded-sm overflow-hidden">
							<ReactQuill
								value={body}
								onChange={setBody}
								modules={quillModules}
								placeholder={t("email.compose.messagePlaceholder", "Write your message...")}
								style={{ minHeight: "200px" }}
							/>
						</div>
					</div>

				{/* Signature preview */}
					{userSignature && (
						<div className="border-t pt-2">
							<p className="text-xs text-muted-foreground mb-1">
								{t("email.compose.signaturePreview", "Signature (auto-appended)")}
							</p>
							<div
								className="text-sm text-gray-600 dark:text-gray-400 pl-2 border-l-2 border-gray-200 dark:border-gray-700"
								dangerouslySetInnerHTML={{ __html: userSignature }}
							/>
						</div>
					)}

					{/* Attach PDF checkbox (only if job is provided) */}
					{job && (
						<div className="flex items-center gap-2">
							<Checkbox
								id="attach-pdf"
								checked={attachPdf}
								onCheckedChange={(checked) => setAttachPdf(checked === true)}
							/>
							<label htmlFor="attach-pdf" className="text-sm flex items-center gap-1.5 cursor-pointer">
								<Paperclip className="h-3.5 w-3.5" />
								{t("email.compose.attachJobDesc", "Attach job description")}
							</label>
						</div>
					)}

					{/* Send Progress */}
					{sendProgress && (
						<div className="text-sm text-gray-600 dark:text-gray-400">
							{t("email.compose.progress", "Sent {{sent}} of {{total}}", {
								sent: sendProgress.sent,
								total: sendProgress.total,
							})}
						</div>
					)}

					{/* Actions */}
					<div className="flex justify-end gap-2 pt-2 border-t">
						<Button variant="outline" onClick={onClose} disabled={isSending}>
							{t("common.cancel", "Cancel")}
						</Button>
						<Button onClick={handleSend} disabled={!canSend}>
							{isSending ? (
								<>
									<Loader2 className="h-4 w-4 mr-2 animate-spin" />
									{t("email.compose.sending", "Sending...")}
								</>
							) : (
								<>
									<Send className="h-4 w-4 mr-2" />
									{validRecipients.length > 1
										? t("email.compose.sendBulk", "Send to {{count}} recipients", { count: validRecipients.length })
										: t("email.compose.send", "Send")}
								</>
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
